import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import type {
  AssistantBrowserCommand,
  AssistantBrowserCommandPayload,
  AssistantBrowserCommandType,
} from './assistant-browser-commands.generated'

type CommandDefinition<T extends AssistantBrowserCommandType> = {
  readonly type: T
}

type CommandHandler<T extends AssistantBrowserCommandType> = (
  payload: AssistantBrowserCommandPayload<T>,
  signal: AbortSignal,
) => boolean | Promise<boolean>

type CommandMatcher<T extends AssistantBrowserCommandType> = (
  payload: AssistantBrowserCommandPayload<T>,
) => boolean

type Registration = {
  matches: (payload: unknown) => boolean
  handle: (payload: unknown, signal: AbortSignal) => boolean | Promise<boolean>
}

class AssistantBrowserCommandRegistry {
  private readonly registrations = new Map<AssistantBrowserCommandType, Set<Registration>>()

  register<T extends AssistantBrowserCommandType>(
    type: T,
    handler: CommandHandler<T>,
    matches: CommandMatcher<T>,
  ) {
    const registration: Registration = {
      matches: (payload) => matches(payload as AssistantBrowserCommandPayload<T>),
      handle: (payload, signal) => handler(payload as AssistantBrowserCommandPayload<T>, signal),
    }
    const handlers = this.registrations.get(type) ?? new Set()
    handlers.add(registration)
    this.registrations.set(type, handlers)
    return () => {
      handlers.delete(registration)
      if (handlers.size === 0) this.registrations.delete(type)
    }
  }

  async dispatch(command: AssistantBrowserCommand, signal: AbortSignal) {
    const registrations = [...(this.registrations.get(command.type) ?? [])]
      .filter((registration) => registration.matches(command.payload))
    if (registrations.length !== 1) return false
    return registrations[0]!.handle(command.payload, signal)
  }
}

const AssistantBrowserCommandContext = createContext<AssistantBrowserCommandRegistry | null>(null)

export function AssistantBrowserCommandProvider({ children }: { children: ReactNode }) {
  const [registry] = useState(() => new AssistantBrowserCommandRegistry())
  return (
    <AssistantBrowserCommandContext.Provider value={registry}>
      {children}
    </AssistantBrowserCommandContext.Provider>
  )
}

export function useAssistantBrowserCommand<T extends AssistantBrowserCommandType>(
  definition: CommandDefinition<T>,
  handler: CommandHandler<T>,
  matches: CommandMatcher<T> = () => true,
) {
  const registry = useContext(AssistantBrowserCommandContext)
  const handlerRef = useRef(handler)
  const matchesRef = useRef(matches)
  useLayoutEffect(() => {
    handlerRef.current = handler
    matchesRef.current = matches
  })
  useLayoutEffect(() => {
    if (!registry) return
    return registry.register(
      definition.type,
      (payload, signal) => handlerRef.current(payload, signal),
      (payload) => matchesRef.current(payload),
    )
  }, [definition.type, registry])
}

export function useAssistantBrowserCommandDispatcher() {
  const registry = useContext(AssistantBrowserCommandContext)
  return (command: AssistantBrowserCommand, signal: AbortSignal) =>
    registry?.dispatch(command, signal) ?? Promise.resolve(false)
}
